// ChartTablePair: shared layout wrapper for chart + collapsible table pairs.
// Chart and table are passed as React nodes; each section keeps its own Nivo config.

import React from "react";
import { Button, Collapse, Typography } from "@material-tailwind/react";
import { ResponsiveBar } from "@nivo/bar";
import { BasicTooltip } from "@nivo/tooltip";
import {
    truncateLabel,
    chartTheme,
    tooltipLabelMap,
    CHART_BASE_MARGIN,
    CHART_Y_AXIS_LABEL_CLASS,
} from "../data-transforms";

/**
 * Nivo ResponsiveBar props shared by every chart in the Survey sub-tab.
 * Per-chart config spreads this and overrides data, keys, colors,
 * margin, axisBottom.legend, legends, tooltip, labelPosition, etc.
 */
export const COMMON_CHART_PROPS = {
    indexBy: "label",
    layout: "horizontal",
    padding: 0.3,
    valueScale: { type: "linear" },
    indexScale: { type: "band", round: true },
    borderColor: { from: "color", modifiers: [["darker", 1.6]] },
    axisTop: null,
    axisRight: null,
    axisLeft: {
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        format: (value) => truncateLabel(value, 10),
    },
    labelSkipWidth: 12,
    labelSkipHeight: 12,
    labelTextColor: "#000",
    animate: true,
    motionStiffness: 90,
    motionDamping: 15,
    theme: chartTheme,
};

/**
 * Build the standard Nivo legend block used by the two "total
 * engagement" charts (Question Engagement and Theme Engagement).
 * Each entry pairs an internal series id with its display label and
 * bar color, taken from tooltipLabelMap and the matching colors array.
 *
 * @param {string[]} keys - series ids (e.g. ["contributors", "responses", "uniqueResponses"])
 * @param {string[]} colors - parallel color hex strings
 * @param {object} legendHelpers - { legendDirection, getLegendPosition, getLegendItemWidth }
 * @returns {object[]} Nivo legends prop (single-element array)
 */
export function buildEngagementLegend(keys, colors, { legendDirection, getLegendPosition, getLegendItemWidth }) {
    return [
        {
            data: keys.map((id, i) => ({
                id,
                label: tooltipLabelMap[id] || id,
                color: colors[i],
            })),
            anchor: "bottom-left",
            direction: legendDirection,
            justify: false,
            translateY: getLegendPosition(keys.length).translateY,
            itemsSpacing: 2,
            itemWidth: getLegendItemWidth(),
            itemHeight: 20,
            itemDirection: "left-to-right",
            itemOpacity: 0.85,
            symbolSize: 20,
            effects: [
                {
                    on: "hover",
                    style: {
                        itemOpacity: 1,
                    },
                },
            ],
        },
    ];
}

/**
 * Standard tooltip renderer used by all chart variants that have one.
 * The truncate length is parameterised because the original code used
 * 20-char truncation for question charts and 10-char for the theme
 * total engagement chart.
 *
 * @param {number} truncateLength - characters to keep before "..."
 */
export function makeChartTooltip(truncateLength = 20) {
    return ({ indexValue, value, color, id }) => (
        <BasicTooltip
            id={`${truncateLabel(indexValue, truncateLength)} - ${tooltipLabelMap[id] || id}: ${value}`}
            enableChip={true}
            color={color}
        />
    );
}

/**
 * Wrap a chart + table in the standard "heading + chart + toggle
 * button + collapse" pattern used by every sub-section of the Survey
 * Question Charts and Survey Theme Charts sections.
 *
 * The chart and table are passed as React nodes so callers keep full
 * control over Nivo configuration and table column layout. The
 * `visible` prop controls whether the chart/button/table render at
 * all (the heading always renders).
 *
 * @param {object} props
 * @param {string} props.title - sub-section heading text
 * @param {boolean} props.visible - whether to render chart/button/table
 * @param {string} props.yAxisLabel - absolutely-positioned label, e.g. "Questions"
 * @param {number|string} props.chartHeight - CSS height for the chart container
 * @param {React.ReactNode} props.chart - fully-configured ResponsiveBar element
 * @param {React.ReactNode} props.table - fully-configured GobbyTable element
 * @param {string} props.toggleKey - key into openChartTables (e.g. "surveyQuestionEngagement")
 * @param {object} props.openChartTables - map of toggle key -> boolean
 * @param {(key: string) => void} props.toggleChartTable - toggle callback
 * @param {string} [props.className] - optional className for the wrapper div
 */
export function ChartTablePair({
    title,
    visible,
    yAxisLabel,
    chartHeight,
    chart,
    table,
    toggleKey,
    openChartTables,
    toggleChartTable,
    className = "",
}) {
    return (
        <div className={className}>
            <Typography variant="h6">
                {title}
            </Typography>
            {visible && (
                <>
                    <div className="bg-white rounded-lg relative" style={{ height: chartHeight }}>
                        <span className={CHART_Y_AXIS_LABEL_CLASS}>{yAxisLabel}</span>
                        {chart}
                    </div>
                    <Button
                        onClick={() => toggleChartTable(toggleKey)}
                        variant="outlined"
                        size="sm"
                        className="mt-2"
                    >
                        {openChartTables[toggleKey] ? "Hide" : "Show"} table data
                    </Button>
                    <Collapse open={openChartTables[toggleKey] || false}>
                        <div className="mt-2">
                            {table}
                        </div>
                    </Collapse>
                </>
            )}
        </div>
    );
}

// Re-export the underlying Nivo components so section files can import
// everything they need from this one module.
export { ResponsiveBar, BasicTooltip, CHART_BASE_MARGIN };
